import { api } from './client';

// 객실 변경 이력 타입
export interface RoomAssignmentHistoryEntry {
  changedAt: string;
  previousRoomId: string;
  newRoomId: string;
  changedBy?: string;
  reason?: string;
}

// 로그 엔트리 타입
export interface LogEntry {
  date: string;
}

export interface Reservation {
  reservationId: string;
  roomId: string;
  roomType: string;
  guestName: string;
  guestPhone?: string;
  guestEmail?: string;
  checkinDate: string;
  checkoutDate: string;
  nights: number;
  adults: number;
  children: number;
  sellingPrice: number;
  receivedPrice: number;
  status: 'confirmed' | 'cancelled' | 'no_show';
  platform: string;
  checkinTime?: string;
  checkoutTime?: string;
  isLateCheckout: boolean;
  lateCheckoutTime?: string;
  isEarlyCheckin: boolean;
  earlyCheckinTime?: string;
  specialRequests?: string;
  checkinMemo?: string;
  checkoutMemo?: string;
  adminMemo?: string;
  room?: Room;
  guestParking?: GuestParking[];
  // 로그 필드
  roomAssignmentHistory?: RoomAssignmentHistoryEntry[];
  readCheckinWebpage?: boolean;
  readCheckinWebpageLog?: LogEntry[];
  readParkingWebpage?: boolean;
  readParkingWebpageLog?: LogEntry[];
  parkingRegistrationLog?: LogEntry[];
  // 턴오버 타임 (입실일에만)
  turnoverTime?: {
    formatted: string;
    totalMinutes: number;
  } | null;
  // 청소 상태
  cleaningStatus?: string;
}

export interface Room {
  roomId: string;
  building: string;
  roomNumber: string;
  roomAddress: string;
  roomType: string;
  maxOccupancy: number;
  standardOccupancy: number;
  status: 'available' | 'maintenance' | 'inactive';
  features?: string;
  password?: string;
  createdAt?: string;
  updatedAt?: string;
}

export interface GuestParking {
  id: number;
  carNumber: string;
}

export interface CreateReservationData {
  reservationId?: string;  // 선택적 예약 ID (입력 시 해당 ID 사용, 미입력 시 자동 생성)
  roomId: string;
  roomType: string;
  guestName: string;
  guestPhone?: string;
  guestEmail?: string;
  checkinDate: string;
  checkoutDate: string;
  checkinTime?: string;
  checkoutTime?: string;
  sellingPrice: number;
  receivedPrice: number;
  platform: string;
  adults?: number;
  children?: number;
  specialRequests?: string;
  checkinMemo?: string;
  checkoutMemo?: string;
  adminMemo?: string;
  isLateCheckout?: boolean;
  lateCheckoutTime?: string;
  isEarlyCheckin?: boolean;
  earlyCheckinTime?: string;
}

export const reservationsApi = {
  getReservations: async (params?: {
    date?: string;
    startDate?: string;
    endDate?: string;
    status?: string;
    roomId?: string;
    platform?: string;
  }) => {
    const response = await api.get('/reservations', { params });
    return response.data.data as Reservation[];
  },

  getActiveReservations: async (date: string) => {
    const response = await api.get(`/reservations/active/${date}`);
    return response.data.data as Reservation[];
  },

  getReservationById: async (id: string) => {
    const response = await api.get(`/reservations/${id}`);
    return response.data.data as Reservation;
  },

  createReservation: async (data: CreateReservationData) => {
    const response = await api.post('/reservations', data);
    return response.data.data as Reservation;
  },

  updateReservation: async (id: string, data: Partial<CreateReservationData>) => {
    const response = await api.put(`/reservations/${id}`, data);
    return response.data.data as Reservation;
  },

  cancelReservation: async (id: string) => {
    const response = await api.delete(`/reservations/${id}`);
    return response.data;
  },

  updateStatus: async (id: string, status: string) => {
    const response = await api.put(`/reservations/${id}/status`, { status });
    return response.data.data as Reservation;
  },

  updateRoom: async (id: string, roomId: string, reason?: string) => {
    const response = await api.put(`/reservations/${id}/room`, { roomId, reason });
    return response.data.data as Reservation;
  },

  clearVehicles: async (id: string) => {
    const response = await api.delete(`/reservations/${id}/vehicles`);
    return response.data;
  },

  getAvailableRooms: async (date: string) => {
    const response = await api.get(`/reservations/available/${date}`);
    return response.data.data as Room[];
  },

  getCancelledReservations: async (date: string) => {
    const response = await api.get(`/reservations/cancelled/${date}`);
    return response.data.data as Reservation[];
  },
};
